import React, { useState } from "react";
import { Card, Nav, Form, Button } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { useAuth } from "../Context/AuthContext";
import { usePreferences } from "../Context/PreferencesContext";
import { useToast } from "../Context/useToast";
import { DATE_PRESETS, formatDate, formatMoney, shouldAccentNegative } from "../utils/formatters";

const TABS = [
   { key: "profile", label: "Profile" },
   { key: "appearance", label: "Appearance" },
   { key: "formats", label: "Formats" },
   { key: "security", label: "Security" },
];

// Card header: explicit block elements so the title and description always stack
// (React Bootstrap's Card.Title/Card.Text render inline in this setup).
function CardHead({ title, text }) {
   return (
      <Card.Header>
         <h6 className="card-title mb-1">{title}</h6>
         <p className="card-text mb-0">{text}</p>
      </Card.Header>
   );
}

// One label + description + control row, matching the template's settings layout.
function SettingRow({ title, description, children }) {
   return (
      <div className="setting-item d-md-flex align-items-center justify-content-between">
         <div className="me-md-4 mb-2 mb-md-0">
            <h6>{title}</h6>
            {description && <p>{description}</p>}
         </div>
         <div className="setting-item-control">{children}</div>
      </div>
   );
}

function ProfileTab({ userClaims }) {
   const name = [userClaims?.fn, userClaims?.ln].filter(Boolean).join(" ") || userClaims?.name || "—";
   const email = userClaims?.email || "—";
   const role = userClaims?.rol_name || userClaims?.role || null;
   const timeZone = userClaims?.u_time_zone || userClaims?.timeZone || null;

   return (
      <Card className="card-settings">
         <CardHead title="Your profile" text="Account details from your sign-in. To change these, contact your administrator." />
         <Card.Body className="p-0">
            <SettingRow title="Name"><span className="setting-value">{name}</span></SettingRow>
            <SettingRow title="Email"><span className="setting-value">{email}</span></SettingRow>
            {role && <SettingRow title="Role"><span className="setting-value">{role}</span></SettingRow>}
            {timeZone && <SettingRow title="Time zone"><span className="setting-value">{timeZone}</span></SettingRow>}
         </Card.Body>
      </Card>
   );
}

function AppearanceTab({ prefs, setTheme }) {
   return (
      <Card className="card-settings">
         <CardHead title="Appearance" text="Choose how Rekonsil looks. Your choice is saved to your account." />
         <Card.Body className="p-0">
            <SettingRow title="Theme" description="Switch between a light and dark interface.">
               <div className="btn-group" role="group" aria-label="Theme">
                  <Button
                     variant={prefs.theme !== "dark" ? "primary" : "outline-primary"}
                     onClick={() => setTheme("light")}
                  >
                     Light
                  </Button>
                  <Button
                     variant={prefs.theme === "dark" ? "primary" : "outline-primary"}
                     onClick={() => setTheme("dark")}
                  >
                     Dark
                  </Button>
               </div>
            </SettingRow>
         </Card.Body>
      </Card>
   );
}

function FormatPreview({ prefs }) {
   const samples = [-1234.33, 1234.5, 0];
   const accent = (v) => (shouldAccentNegative(v, prefs.money) ? { color: "#dc3545" } : undefined);
   return (
      <div className="settings-preview">
         <div className="settings-preview-label">Preview</div>
         <div className="settings-preview-row">
            <span>Date</span>
            <strong>{formatDate(new Date(), prefs.dateFormat)}</strong>
         </div>
         {samples.map((v) => (
            <div className="settings-preview-row" key={v}>
               <span>{v < 0 ? "Credit" : "Amount"}</span>
               <strong style={accent(v)}>{formatMoney(v, prefs.money)}</strong>
            </div>
         ))}
      </div>
   );
}

function FormatsTab({ prefs, setDateFormat, setMoneyFormat }) {
   const negStyle = prefs.money?.negativeStyle || "minus";
   return (
      <Card className="card-settings">
         <CardHead title="Date & number formats" text="Control how dates and amounts appear across the app. Changes save automatically." />
         <Card.Body className="p-0">
            <SettingRow title="Date format" description="Used everywhere dates are shown.">
               <Form.Select
                  value={prefs.dateFormat}
                  onChange={(e) => setDateFormat(e.target.value)}
                  style={{ minWidth: 200 }}
                  aria-label="Date format"
               >
                  {DATE_PRESETS.map((p) => (
                     <option key={p.value} value={p.value}>
                        {p.label}
                     </option>
                  ))}
               </Form.Select>
            </SettingRow>

            <SettingRow title="Credits & negative amounts" description="How negative amounts are displayed.">
               <div className="btn-group" role="group" aria-label="Negative amount style">
                  <Button
                     variant={negStyle === "minus" ? "primary" : "outline-primary"}
                     onClick={() => setMoneyFormat({ negativeStyle: "minus" })}
                  >
                     -1,234.33
                  </Button>
                  <Button
                     variant={negStyle === "parens" ? "primary" : "outline-primary"}
                     onClick={() => setMoneyFormat({ negativeStyle: "parens" })}
                  >
                     (1,234.33)
                  </Button>
               </div>
            </SettingRow>

            <SettingRow title="Show negatives in red" description="Highlight credits and negative amounts.">
               <Form.Check
                  type="switch"
                  id="redNegatives"
                  checked={!!prefs.money?.redNegatives}
                  onChange={(e) => setMoneyFormat({ redNegatives: e.target.checked })}
                  aria-label="Show negatives in red"
               />
            </SettingRow>

            <div className="setting-item">
               <FormatPreview prefs={prefs} />
            </div>
         </Card.Body>
      </Card>
   );
}

function SecurityTab({ changePassword }) {
   const { triggerToast } = useToast();
   const [current, setCurrent] = useState("");
   const [next, setNext] = useState("");
   const [confirm, setConfirm] = useState("");
   const [error, setError] = useState("");
   const [busy, setBusy] = useState(false);

   const reset = () => {
      setCurrent("");
      setNext("");
      setConfirm("");
   };

   const onSubmit = async (e) => {
      e.preventDefault();
      setError("");
      if (next.length < 6) {
         setError("New password must be at least 6 characters.");
         return;
      }
      if (next !== confirm) {
         setError("New password and confirmation don't match.");
         return;
      }
      setBusy(true);
      const res = await changePassword(current, next);
      setBusy(false);
      if (res.st === 1) {
         reset();
         triggerToast("Password changed", "success");
      } else {
         setError(res.msg || "Couldn't change password. Please try again.");
      }
   };

   return (
      <Card className="card-settings">
         <CardHead title="Change password" text="Update the password you use to sign in." />
         <Card.Body>
            <Form onSubmit={onSubmit} style={{ maxWidth: 420 }}>
               {error && (
                  <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
                     {error}
                  </div>
               )}
               <Form.Group className="mb-3">
                  <Form.Label>Current password</Form.Label>
                  <Form.Control
                     type="password"
                     value={current}
                     onChange={(e) => setCurrent(e.target.value)}
                     autoComplete="current-password"
                     required
                  />
               </Form.Group>
               <Form.Group className="mb-3">
                  <Form.Label>New password</Form.Label>
                  <Form.Control
                     type="password"
                     value={next}
                     onChange={(e) => setNext(e.target.value)}
                     autoComplete="new-password"
                     required
                  />
               </Form.Group>
               <Form.Group className="mb-4">
                  <Form.Label>Confirm new password</Form.Label>
                  <Form.Control
                     type="password"
                     value={confirm}
                     onChange={(e) => setConfirm(e.target.value)}
                     autoComplete="new-password"
                     required
                  />
               </Form.Group>
               <Button type="submit" variant="primary" disabled={busy || !current || !next || !confirm}>
                  {busy ? "Changing…" : "Change password"}
               </Button>
            </Form>
         </Card.Body>
      </Card>
   );
}

export default function Settings() {
   const { userClaims, changePassword } = useAuth();
   const { prefs, setTheme, setDateFormat, setMoneyFormat } = usePreferences();
   const [tab, setTab] = useState("profile");

   return (
      <React.Fragment>
         <Header />
         <div className="main main-app settings-page p-3 p-lg-4 p-xxl-5">
            <h2 className="main-title mb-1">Settings</h2>
            <p className="text-secondary mb-4">Manage your profile, appearance, formats, and password.</p>

            {/* Controlled Nav — activeKey/eventKey so only the selected tab is active.
                (Sharing href="#" across Nav.Links made RB mark them all active → all underlined.) */}
            <Nav className="nav-line mb-4" activeKey={tab} onSelect={(k) => k && setTab(k)}>
               {TABS.map((t) => (
                  <Nav.Link key={t.key} eventKey={t.key}>
                     {t.label}
                  </Nav.Link>
               ))}
            </Nav>

            {tab === "profile" && <ProfileTab userClaims={userClaims} />}
            {tab === "appearance" && <AppearanceTab prefs={prefs} setTheme={setTheme} />}
            {tab === "formats" && (
               <FormatsTab prefs={prefs} setDateFormat={setDateFormat} setMoneyFormat={setMoneyFormat} />
            )}
            {tab === "security" && <SecurityTab changePassword={changePassword} />}

            <Footer />
         </div>
      </React.Fragment>
   );
}
